"use client";

import { motion } from "motion/react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { HomeBlock, WidgetConfig, WidgetTab } from "@/lib/widget-config";
import { AskQuestionBlock } from "./blocks/ask-question";
import { FeaturedArticleBlock } from "./blocks/featured-article";
import { HelpSearchBlock } from "./blocks/help-search";
import { NewsPreviewBlock } from "./blocks/news-preview";
import { PinnedArticlesBlock } from "./blocks/pinned-articles";
import { QuickActionsBlock } from "./blocks/quick-actions";
import { RecentMessageBlock } from "./blocks/recent-message";
import { RichTextBlock } from "./blocks/rich-text";
import { TeamStatusBlock } from "./blocks/team-status";

/**
 * Home-tab block renderer.
 *
 * The Home tab is an operator-configured ordered list of `HomeBlock`s
 * (see `WidgetConfig.homeLayout` / `DEFAULT_HOME_LAYOUT`). This file
 * dispatches that list — one block component per `kind` (see `blocks/`).
 * Unknown kinds are skipped so a newer dashboard can't break an older
 * bundle.
 *
 * Navigation-only v1: blocks that reference Help / News / Messenger
 * content render their structure + a working entry point into the
 * relevant tab. Live per-block data hydration (recent conversations,
 * pinned-article previews, real news rows) is a Phase-2 data layer.
 */
interface HomeBlocksProps {
  blocks: HomeBlock[];
  config: WidgetConfig;
  /** Open a top-level tab. v1 Home blocks are navigation-only entry
   *  points — they route into Help / News / Messenger rather than
   *  hydrating per-block content inline. */
  onOpenTab: (tab: WidgetTab) => void;
  /** Drill into a specific news article by id — used by `featured-article`
   *  to open the post detail rather than dumping the visitor on the
   *  news list. */
  onOpenNewsArticle?: (id: string) => void;
  /** Drill into a specific help article by slug — used by
   *  `pinned-articles` so the visitor lands on the article rather than
   *  the help index. */
  onOpenHelpArticle?: (slug: string) => void;
  /** Demo data mode. When `true`, content-rendering blocks (news,
   *  conversations) fill with bundled samples so the operator dashboard
   *  preview shows a populated widget. When `false`, they fetch real
   *  data and hide / show an empty state when none exists. */
  isDummy: boolean;
  /** Authenticated visitor chat id — needed by `recent-message` to query
   *  the visitor's latest inbound. `null` when the visitor hasn't auth'd
   *  yet (the block hides itself in that state). */
  chatId: string | null;
  /** Open the visitor's conversation directly (a thread), not the thread
   *  list — used by `recent-message`, which is bound to a conversation. */
  onOpenThread?: () => void;
}

export function HomeBlocks({
  blocks,
  config,
  onOpenTab,
  onOpenNewsArticle,
  onOpenHelpArticle,
  isDummy,
  chatId,
  onOpenThread,
}: HomeBlocksProps) {
  return (
    <div className="flex flex-col gap-3 px-4 pb-2">
      {blocks.map((block, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: blocks are operator-ordered and order-stable
        <Stagger key={`${block.kind}-${i}`} delay={Math.min(i, 6) * 0.05}>
          <HomeBlockView
            block={block}
            config={config}
            onOpenTab={onOpenTab}
            onOpenNewsArticle={onOpenNewsArticle}
            onOpenHelpArticle={onOpenHelpArticle}
            isDummy={isDummy}
            chatId={chatId}
            onOpenThread={onOpenThread}
          />
        </Stagger>
      ))}
    </div>
  );
}

function HomeBlockView({
  block,
  config,
  onOpenTab,
  onOpenNewsArticle,
  onOpenHelpArticle,
  isDummy,
  chatId,
  onOpenThread,
}: {
  block: HomeBlock;
  config: WidgetConfig;
  onOpenTab: (tab: WidgetTab) => void;
  onOpenThread?: () => void;
  onOpenNewsArticle?: (id: string) => void;
  /** Drill into a specific help article by slug — used by
   *  `pinned-articles` so the visitor lands on the article rather than
   *  the help index. */
  onOpenHelpArticle?: (slug: string) => void;
  isDummy: boolean;
  chatId: string | null;
}) {
  switch (block.kind) {
    case "quick-actions":
      return <QuickActionsBlock block={block} onOpenTab={onOpenTab} />;
    case "help-search":
      return (
        <HelpSearchBlock
          block={block}
          onOpenTab={onOpenTab}
          onOpenArticle={onOpenHelpArticle}
          isDummy={isDummy}
        />
      );
    case "pinned-articles":
      return (
        <PinnedArticlesBlock
          block={block}
          onOpenTab={onOpenTab}
          onOpenArticle={onOpenHelpArticle}
          isDummy={isDummy}
        />
      );
    case "news-preview":
      return (
        <NewsPreviewBlock
          block={block}
          onOpenTab={onOpenTab}
          onOpenNewsArticle={onOpenNewsArticle}
          isDummy={isDummy}
        />
      );
    case "team-status":
      return <TeamStatusBlock block={block} config={config} />;
    case "rich-text":
      return <RichTextBlock block={block} />;
    case "divider":
      return <hr className="my-1 border-wx-border" />;
    case "ask-question":
      return <AskQuestionBlock block={block} config={config} onOpenTab={onOpenTab} />;
    case "recent-message":
      return (
        <RecentMessageBlock
          block={block}
          onOpenTab={onOpenTab}
          onOpenThread={onOpenThread}
          isDummy={isDummy}
          chatId={chatId}
          config={config}
        />
      );
    case "featured-article":
      return (
        <FeaturedArticleBlock
          block={block}
          onOpenTab={onOpenTab}
          onOpenNewsArticle={onOpenNewsArticle}
          isDummy={isDummy}
        />
      );
    default:
      // Unknown kind from a newer dashboard — skip gracefully.
      return null;
  }
}

function Stagger({
  delay,
  children,
  className,
}: {
  delay: number;
  children: ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, delay, ease: "easeOut" }}
      className={cn(className)}
    >
      {children}
    </motion.div>
  );
}

/**
 * Drop blocks that depend on a disabled feature — `help-search` /
 * `pinned-articles` when Help is off, `news-preview` when News is off.
 * Keeps the operator from shipping dead entry points.
 */
export function filterHomeBlocks(
  blocks: HomeBlock[],
  features: WidgetConfig["features"],
): HomeBlock[] {
  // Operator JSON: tolerate a non-array layout and null / non-object entries.
  if (!Array.isArray(blocks)) return [];
  return blocks.filter((b) => {
    if (!b || typeof b !== "object") return false;
    if ((b.kind === "help-search" || b.kind === "pinned-articles") && !features.help) {
      return false;
    }
    if (b.kind === "news-preview" && !features.news) return false;
    return true;
  });
}
